import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { BACKEND_URL } from "../lib/api";

export default function NewsletterForm({ source = "field-notes" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ok | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, { email, source });
      setStatus("ok");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="rounded-3xl border border-line bg-surface p-8 md:p-10" data-testid="newsletter-form">
      <p className="font-sans text-[11px] uppercase tracking-mega text-brass">The Sunday letter</p>
      <h3 className="mt-3 font-serif text-3xl md:text-4xl text-ink leading-snug max-w-xl">
        Every Sunday, one honest Field Note in your inbox.
      </h3>
      <p className="mt-4 font-serif italic text-lg text-ink-muted max-w-xl leading-relaxed">
        No pitch. No noise. Just a two-minute story from a real founder conversation, delivered.
      </p>

      <AnimatePresence mode="wait">
        {status === "ok" ? (
          <motion.div
            key="ok"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-forest/10 border border-forest/30 px-5 py-3 text-forest"
            data-testid="newsletter-success"
          >
            <Check className="h-4 w-4" />
            <span className="font-sans text-sm">You are in. See you on Sunday.</span>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-6 flex flex-col sm:flex-row gap-3 max-w-lg"
          >
            <input
              type="email"
              required
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 rounded-full border border-line bg-bg px-5 py-3 font-serif text-ink placeholder-ink-muted/60 focus:outline-none focus:border-forest transition-colors"
              data-testid="newsletter-input"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              data-testid="newsletter-submit"
              className="rounded-full bg-forest px-6 py-3 font-sans text-xs uppercase tracking-wider font-semibold text-bg hover:bg-forest-hover transition-colors disabled:opacity-60"
            >
              {status === "loading" ? "Sending" : "Subscribe"}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
      {status === "error" && (
        <p className="mt-3 font-sans text-sm text-red-700">Something went wrong. Please try again.</p>
      )}
    </section>
  );
}
